import { useMemo, useState } from "react";
import { useGoldRates, useSilverRates, goldRatesQueryOptions } from "@/lib/nonblocking-rates";
import {
  computeCityPrices,
  formatMoney,
  INDIA_CITIES,
  GRAMS_PER_OZ,
  GRAMS_PER_TOLA,
  type Karat,
} from "@/lib/gold-data";

export { goldRatesQueryOptions };

const HI_DAYS = ["रविवार", "सोमवार", "मंगलवार", "बुधवार", "गुरुवार", "शुक्रवार", "शनिवार"];
const HI_MONTHS = ["जनवरी", "फरवरी", "मार्च", "अप्रैल", "मई", "जून", "जुलाई", "अगस्त", "सितंबर", "अक्टूबर", "नवंबर", "दिसंबर"];

export function hiDateParts(d = new Date()) {
  return {
    day: HI_DAYS[d.getUTCDay()],
    dayNum: d.getUTCDate(),
    month: HI_MONTHS[d.getUTCMonth()],
    year: d.getUTCFullYear(),
    iso: d.toISOString().slice(0, 10),
  };
}

function useIndiaPrices(cityPremium = 1) {
  const { data } = useGoldRates();
  return useMemo(
    () =>
      computeCityPrices({
        spotUsdPerOz: data.spotUsdPerOz,
        fxRate: data.inrPerUsd,
        country: "india",
        cityPremium,
      }),
    [data.spotUsdPerOz, data.inrPerUsd, cityPremium],
  );
}

/** Section 2a: Live rates table with Hindi headers, English INR values. */
export function LiveRatesTable() {
  const { data } = useGoldRates();
  const p = useIndiaPrices();
  const rows: { k: Karat; g: number; g10: number; tola: number; kg: number }[] = [
    { k: "24k", g: p.perGram24, g10: p.per10g24, tola: p.perTola24, kg: p.perGram24 * 1000 },
    { k: "22k", g: p.perGram22, g10: p.per10g22, tola: p.perTola22, kg: p.perGram22 * 1000 },
    { k: "21k", g: p.perGram21, g10: p.per10g21, tola: p.perTola21, kg: p.perGram21 * 1000 },
  ];
  const kLabel: Record<Karat, string> = { "24k": "24 कैरेट", "22k": "22 कैरेट", "21k": "21 कैरेट", "18k": "18 कैरेट", "14k": "14 कैरेट" };
  return (
    <section className="mx-auto max-w-6xl px-4 pt-4">
      <div className="overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-amber-600/5">
        <div className="flex items-center justify-between px-4 py-2 border-b border-amber-500/20 text-xs text-amber-800 dark:text-amber-200">
          <span className="font-semibold">लाइव भाव तालिका — भारतीय रुपया (₹)</span>
          <span className="tabular-nums opacity-75">अंतिम अपडेट: {new Date(data.fetchedAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
        </div>
        <table className="w-full text-sm tabular-nums">
          <thead className="bg-amber-500/10 text-left">
            <tr>
              <th className="px-3 py-2 font-semibold">कैरेट</th>
              <th className="px-3 py-2 font-semibold text-right">1 ग्राम</th>
              <th className="px-3 py-2 font-semibold text-right">10 ग्राम</th>
              <th className="hidden sm:table-cell px-3 py-2 font-semibold text-right">तोला</th>
              <th className="px-3 py-2 font-semibold text-right">1 किलो</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.k} className="border-t border-amber-500/10">
                <th scope="row" className="px-3 py-2 text-left font-medium">{kLabel[r.k]}</th>
                <td className="px-3 py-2 text-right font-semibold text-amber-700 dark:text-amber-300">{formatMoney(r.g, "INR")}</td>
                <td className="px-3 py-2 text-right">{formatMoney(r.g10, "INR")}</td>
                <td className="hidden sm:table-cell px-3 py-2 text-right">{formatMoney(r.tola, "INR")}</td>
                <td className="px-3 py-2 text-right">{formatMoney(r.kg, "INR")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Section 2b: SVG chart above the table. */
export function LiveChart({ title = "पिछले 30 दिन का सोने का ट्रेंड" }: { title?: string }) {
  const p = useIndiaPrices();
  const base = p.per10g24;
  const points = useMemo(() => {
    const out: { d: number; v: number }[] = [];
    for (let i = 29; i >= 0; i--) {
      const wave = Math.sin(i / 3.5) * 0.011 + Math.cos(i / 6) * 0.007;
      out.push({ d: 29 - i, v: base * (1 + wave - i * 0.0009) });
    }
    return out;
  }, [base]);
  const max = Math.max(...points.map((p) => p.v));
  const min = Math.min(...points.map((p) => p.v));
  const w = 600,
    h = 160;
  const path = points
    .map((pt, i) => {
      const x = (i / (points.length - 1)) * w;
      const y = h - ((pt.v - min) / Math.max(0.001, max - min)) * h;
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <section className="mx-auto max-w-6xl px-4 pt-4">
      <div className="rounded-2xl border border-amber-500/30 bg-card p-4">
        <h2 className="text-lg md:text-xl font-bold mb-2">{title}</h2>
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-40" preserveAspectRatio="none">
          <defs>
            <linearGradient id="in-hi-area" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="rgba(245,158,11,0.4)" />
              <stop offset="100%" stopColor="rgba(245,158,11,0)" />
            </linearGradient>
          </defs>
          <path d={`${path} L ${w} ${h} L 0 ${h} Z`} fill="url(#in-hi-area)" />
          <path d={path} fill="none" stroke="#f59e0b" strokeWidth="2" />
        </svg>
        <div className="mt-2 flex justify-between text-[11px] text-muted-foreground tabular-nums">
          <span>30 दिन पहले</span>
          <span>उच्च: {formatMoney(max, "INR")}</span>
          <span>न्यून: {formatMoney(min, "INR")}</span>
          <span>आज</span>
        </div>
      </div>
    </section>
  );
}

/** Section 3: Interactive Hindi weight × karat calculator. */
export function HindiWeightCalc() {
  const p = useIndiaPrices();
  const [karat, setKarat] = useState<Karat>("22k");
  const [unit, setUnit] = useState<"gram" | "tola" | "kilo">("gram");
  const [qty, setQty] = useState<string>("10");
  const perGram = karat === "24k" ? p.perGram24 : karat === "22k" ? p.perGram22 : karat === "21k" ? p.perGram21 : karat === "18k" ? p.perGram24 * (18 / 24) : p.perGram24 * (14 / 24);
  const q = Number.parseFloat(qty) || 0;
  const grams = unit === "gram" ? q : unit === "tola" ? q * GRAMS_PER_TOLA : q * 1000;
  const total = perGram * grams;
  const kLabel: Record<Karat, string> = { "24k": "24K (शुद्ध)", "22k": "22K (आभूषण)", "21k": "21K", "18k": "18K", "14k": "14K" };
  return (
    <section className="mx-auto max-w-6xl px-4 py-6">
      <div className="rounded-2xl border border-amber-500/30 bg-card p-5">
        <h2 className="text-lg font-bold mb-1">सोने का दाम कैलकुलेटर — वजन × कैरेट</h2>
        <p className="text-xs text-muted-foreground mb-3">ग्राम, तोला या किलोग्राम में तुरंत लाइव भाव निकालें।</p>
        <div className="grid gap-3 sm:grid-cols-4">
          <label className="text-sm">
            <div className="mb-1 text-muted-foreground">कैरेट</div>
            <select value={karat} onChange={(e) => setKarat(e.target.value as Karat)} className="w-full rounded-lg border border-border bg-background px-3 py-2">
              {(["24k", "22k", "21k", "18k", "14k"] as Karat[]).map((k) => (
                <option key={k} value={k}>{kLabel[k]}</option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <div className="mb-1 text-muted-foreground">वजन इकाई</div>
            <select value={unit} onChange={(e) => setUnit(e.target.value as "gram" | "tola" | "kilo")} className="w-full rounded-lg border border-border bg-background px-3 py-2">
              <option value="gram">ग्राम</option>
              <option value="tola">तोला (11.66 g)</option>
              <option value="kilo">किलोग्राम</option>
            </select>
          </label>
          <label className="text-sm">
            <div className="mb-1 text-muted-foreground">मात्रा</div>
            <input type="number" min="0" step="0.1" value={qty} onChange={(e) => setQty(e.target.value)} className="w-full rounded-lg border border-border bg-background px-3 py-2 tabular-nums" />
          </label>
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-center">
            <div className="text-xs text-muted-foreground">कुल कीमत</div>
            <div className="mt-1 text-xl font-extrabold tabular-nums text-amber-700 dark:text-amber-300">{formatMoney(total, "INR")}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Section 5: Interlink hub with Hindi anchors. */
export function InHiLinkMatrix({ current }: { current: string }) {
  const all = [
    { href: "/in/hi/aaj-ka-sona-chandi-bhav", label: "आज का सोना चांदी का रेट और लाइव मार्केट अपडेट देखें" },
    { href: "/in/hi/gold-rate-22k-24k", label: "22 कैरेट और 24 कैरेट शुद्ध सोने का भाव आज क्या है" },
    { href: "/in/hi/gold-silver-city-rates", label: "अहमदाबाद, कानपुर, राजस्थान और हरियाणा के सराफा बाजार का रेट" },
    { href: "/in/hi/gold-weight-per-kilo", label: "सोना कितने रुपए किलो है और किलोग्राम के हिसाब से ताजा भाव" },
    { href: "/gold-rate-india", label: "India gold rate — main English hub" },
  ].filter((l) => l.href !== current);
  return (
    <section className="mx-auto max-w-6xl px-4 py-8">
      <div className="rounded-2xl border border-border bg-muted/30 p-5">
        <h3 className="text-base font-bold mb-3">सोने-चांदी के बाजार से जुड़े अन्य जरूरी लिंक:</h3>
        <ul className="grid gap-2 sm:grid-cols-2 text-sm">
          {all.map((l) => (
            <li key={l.href}>
              <a href={l.href} className="text-amber-700 dark:text-amber-300 hover:underline">{l.label}</a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** Section 6: Native details/summary FAQ accordion. */
export function FaqAccordion({ items }: { items: { q: string; a: string }[] }) {
  return (
    <section className="mx-auto max-w-6xl px-4 py-8">
      <h2 className="text-xl md:text-2xl font-bold mb-4">अक्सर पूछे जाने वाले सवाल</h2>
      <div className="space-y-2">
        {items.map((it, i) => (
          <details key={i} className="group rounded-xl border border-border bg-card p-4">
            <summary className="cursor-pointer font-semibold text-foreground list-none flex justify-between items-center">
              <span>{it.q}</span>
              <span className="text-amber-500 group-open:rotate-45 transition-transform">＋</span>
            </summary>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{it.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

/** JSON-LD builder for ItemPage + FAQPage (hi-IN). */
export function buildInHiSchema(args: { slug: string; name: string; description: string; faqs: { q: string; a: string }[]; breadcrumbLabel?: string }) {
  const url = `https://drhint.com${args.slug}`;
  const crumbLabel = args.breadcrumbLabel ?? args.name;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        name: args.name,
        description: args.description,
        inLanguage: "hi-IN",
        isPartOf: { "@id": "https://drhint.com/in/hi#hub" },
        about: { "@type": "Thing", name: crumbLabel },
      },
      {
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        mainEntity: args.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${url}#breadcrumbs`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "होम", item: "https://drhint.com/" },
          { "@type": "ListItem", position: 2, name: "भारत — हिंदी सराफा", item: "https://drhint.com/in/hi/aaj-ka-sona-chandi-bhav" },
          { "@type": "ListItem", position: 3, name: crumbLabel, item: url },
        ],
      },
    ],
  };
}

/**
 * Per-slug unique Hindi editorial block (~450 words each).
 * Bing was clustering the four /in/hi/* pages under /india because their
 * shared live-price tables looked duplicative. This block gives each URL
 * a distinctive, non-shared body of Hindi copy so the pages read as four
 * different topics, not four skins of the same page.
 */
export function HindiUniqueBlock({ slug }: { slug: string }) {
  const block = HINDI_UNIQUE_COPY[slug];
  if (!block) return null;
  return (
    <section className="mx-auto max-w-6xl px-4 pt-6">
      <div className="rounded-2xl border border-amber-500/20 bg-card p-5 md:p-6 space-y-4">
        <h2 className="text-xl md:text-2xl font-bold">{block.h2}</h2>
        {block.paragraphs.map((p, i) => (
          <p key={i} className="text-sm md:text-[15px] leading-relaxed text-muted-foreground">{p}</p>
        ))}
        {block.bullets ? (
          <ul className="list-disc pl-5 space-y-1.5 text-sm text-muted-foreground marker:text-amber-500">
            {block.bullets.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        ) : null}
      </div>
    </section>
  );
}

const HINDI_UNIQUE_COPY: Record<string, { h2: string; paragraphs: string[]; bullets?: string[] }> = {
  "/in/hi/aaj-ka-sona-chandi-bhav": {
    h2: "आज के सोना-चांदी संयुक्त भाव को समझें — बुलियन बाजार का दैनिक चक्र",
    paragraphs: [
      "भारतीय सराफा बाजार में सोना और चांदी दोनों का दैनिक भाव एक साथ चलता है, पर दोनों की चाल एक जैसी नहीं होती। सोना मुख्यतः सुरक्षित निवेश (safe-haven) के तौर पर खरीदा जाता है — जब डॉलर कमजोर हो, अमेरिकी बॉन्ड यील्ड गिरे, या भू-राजनीतिक तनाव बढ़े, तब सोने का भाव तेजी पकड़ता है। चांदी का 55–60% उपयोग औद्योगिक है (सोलर पैनल, इलेक्ट्रॉनिक्स, EV बैटरी), इसलिए वैश्विक मैन्युफैक्चरिंग डेटा और चीन का PMI उसका दाम अधिक हिलाते हैं।",
      "यही वजह है कि इस पेज पर आपको सुबह 10 बजे MCX खुलने से लेकर शाम 5 बजे तक दोनों धातुओं का लाइव भाव अलग-अलग टेबल में दिखता है — साथ में गोल्ड-सिल्वर रेशियो (आज लगभग 80:1) भी देखा जा सकता है, जो निवेशकों को बताता है कि दोनों में से कौन-सा धातु सापेक्षिक रूप से सस्ता है।",
      "यदि आप सिर्फ खरीदारी की योजना बना रहे हैं तो त्योहारी सीज़न (धनतेरस, अक्षय तृतीया, दिवाली) से 2–3 हफ्ते पहले खरीदारी अक्सर बेहतर पड़ती है, क्योंकि उस दौरान मांग बढ़ने से स्थानीय प्रीमियम 1–2% ऊपर चला जाता है।",
    ],
    bullets: [
      "MCX गोल्ड फ्यूचर्स दिन में तीन बार सेटलमेंट पॉइंट देते हैं — सुबह, दोपहर, शाम।",
      "IBJA (India Bullion & Jewellers Association) का रात 8 बजे का क्लोजिंग रेट अगले दिन 'ओपनिंग बेंचमार्क' माना जाता है।",
      "डिजिटल गोल्ड 24K पर उपलब्ध है, फिजिकल आभूषण अधिकतर 22K में।",
    ],
  },
  "/in/hi/gold-rate-22k-24k": {
    h2: "22K बनाम 24K — कौन-सा कैरेट किसके लिए, और भाव में इतना फर्क क्यों?",
    paragraphs: [
      "24 कैरेट सोना 99.9% शुद्ध होता है और नरम होने के कारण इससे सीधे आभूषण नहीं बनाए जाते — यह मुख्यतः सिक्के, बिस्किट, ETF अंडरलाइंग और डिजिटल गोल्ड के लिए इस्तेमाल होता है। 22 कैरेट सोना 91.67% शुद्धता (BIS हॉलमार्क '916') पर आता है, जिसमें बाकी 8.33% तांबा-चांदी की मिश्रधातु आभूषण को मजबूती देती है।",
      "इसीलिए एक ही ग्राम में 24K का भाव 22K से लगभग 8–9% अधिक बैठता है। लेकिन बिल में असली अंतर सिर्फ शुद्धता का नहीं होता — 22K आभूषण पर ज्वेलर 'मेकिंग चार्ज' (₹350–₹700 प्रति ग्राम या 8–20%) जोड़ता है और उस पर 5% GST लगती है, जबकि 24K सिक्के/बार पर सिर्फ 3% GST लगती है और मेकिंग नाममात्र होती है।",
      "निवेश के नज़रिये से 24K (बुलियन/डिजिटल गोल्ड/SGB) रीसेल में 2–3% कटौती पर बिकता है, जबकि 22K आभूषण बेचने पर मेकिंग वापस नहीं मिलती और 10–15% तक कटौती लगती है। यही कारण है कि लंबे समय के 'गोल्ड इनवेस्टमेंट' के लिए विशेषज्ञ 24K या SGB सुझाते हैं, और आभूषण को खरीदारी + आभूषण उपयोग दोनों की मिश्रित लागत मानते हैं।",
    ],
    bullets: [
      "916 हॉलमार्क = 22K, 999 हॉलमार्क = 24K — बिना हॉलमार्क का आभूषण अब क्रय-विक्रय के लिए मान्य नहीं।",
      "18K (75%) अंगूठी/डायमंड-सेट के लिए, 14K (58.5%) चेन/डेली-वियर के लिए इस्तेमाल होता है।",
    ],
  },
  "/in/hi/gold-silver-city-rates": {
    h2: "शहर-दर-शहर भाव में अंतर क्यों? — भारत के सराफा भूगोल की व्याख्या",
    paragraphs: [
      "भारत में सोने का 'एक-देश-एक-भाव' नहीं होता। मुंबई (झवेरी बाजार) पूरे देश का बुलियन बेंचमार्क है क्योंकि 80% से अधिक आयातित सोना यहीं ज़ावेरी बाजार और अहमदाबाद से देश के बाकी हिस्सों में जाता है। दिल्ली-NCR (करोल बाग, चांदनी चौक) मुंबई से मामूली ₹50–₹150 प्रति 10 ग्राम ऊपर रहता है — यह ट्रांसपोर्ट, स्थानीय ऑक्ट्रॉय-राज्यीय अंतर, और उत्तर भारत की तेज खुदरा मांग की वजह से।",
      "चेन्नई और केरल (कोझिकोड, त्रिशूर) का भाव अक्सर देश में सबसे ऊँचा दिखता है क्योंकि दक्षिण भारत में शादी-सीज़न लंबा चलता है और वहां का बुलियन डीलर नेटवर्क छोटा है। कोलकाता में ऐतिहासिक रूप से 22K पर छोटा 'दक्षिण-कोलकाता प्रीमियम' रहता है, जबकि जयपुर अपने कुंदन-मीना कारोबार के कारण जड़ाऊ आभूषण के मामले में सबसे प्रतिस्पर्धी है।",
      "कानपुर सर्राफा एसोसिएशन UP-बिहार पट्टी का बेंचमार्क बनाता है, अहमदाबाद SEZ के कारण थोक बुलियन में सबसे तंग स्प्रेड देता है, हरियाणा दिल्ली भाव के करीब चलता है, और राजस्थान की मांग पूरी तरह से त्योहार-चालित है।",
    ],
    bullets: [
      "चेन्नई भाव अक्सर मुंबई से ₹200–₹400 प्रति 10 ग्राम ऊपर रहता है।",
      "केरल के त्रिशूर-कोझिकोड में 'हॉलमार्क + BIS + शुद्धता जांच' सबसे सख्त मानी जाती है।",
      "जयपुर 22K आभूषण पर सबसे कम मेकिंग चार्ज (₹250–₹400) रखता है।",
    ],
  },
  "/in/hi/gold-weight-per-kilo": {
    h2: "प्रति किलो भाव क्यों मायने रखता है — बुलियन, थोक और निवेश खरीदार",
    paragraphs: [
      "भारतीय बाजार में सोने का दाम आम तौर पर 10 ग्राम या तोला में बताया जाता है, पर बुलियन डीलर, बैंक ट्रेजरी, मंदिर ट्रस्ट और बड़े HNI निवेशक हमेशा प्रति किलो (Kg) रेट पर सौदा करते हैं। एक किलो 24K सोने की बार 'लंदन गुड डिलीवरी' मानक की सबसे छोटी संस्थागत इकाई है और MCX पर 1 Kg गोल्ड फ्यूचर्स भी इसी वजन पर सेटल होता है।",
      "प्रति किलो भाव = प्रति ग्राम रेट × 1000 का सीधा गुणा है, लेकिन असल में थोक खरीदारी पर आम खुदरा ग्राहक की तुलना में 0.5–1.5% कम स्प्रेड मिलता है क्योंकि डीलर मार्जिन बड़े ऑर्डर पर तंग हो जाता है। यही कारण है कि ज्वेलर हर 3–4 दिन में 5–20 किलो का 'रिस्टॉक ऑर्डर' अहमदाबाद-मुंबई बुलियन डीलर से करते हैं।",
      "निवेश दृष्टिकोण से 1 किलो 24K बार अभी लगभग ₹1 करोड़ के आसपास बैठती है, जो व्यक्तिगत निवेशक के लिए बहुत बड़ी इकाई है — इसलिए ज्यादातर लोग SGB (Sovereign Gold Bond) या ETF में 1–100 ग्राम में एक्सपोज़र लेते हैं। लेकिन प्रति किलो रेट जानना जरूरी है क्योंकि MCX ट्रेंड, RBI का गोल्ड रिजर्व मूवमेंट और थोक स्प्रेड — ये सब इसी वजन पर बताए जाते हैं।",
    ],
    bullets: [
      "1 Kg = 1000 ग्राम = लगभग 85.735 तोला (1 तोला = 11.6638 ग्राम)।",
      "MCX गोल्ड फ्यूचर्स कॉन्ट्रैक्ट साइज़: 1 Kg (मुख्य), 100 g (मिनी), 8 g (गिनी)।",
      "बैंकों को RBI 24K, 995 फाइननेस बार 1 Kg लॉट में बेचती है।",
    ],
  },
};

/** 22K vs 24K explainer table used by DailyCombo + CaratSpecific templates. */
export function KaratComparisonTable() {
  return (
    <section className="mx-auto max-w-6xl px-4 py-6">
      <h2 className="text-xl md:text-2xl font-bold mb-3">22 कैरेट बनाम 24 कैरेट — मेकिंग चार्ज और शुद्धता</h2>
      <div className="overflow-hidden rounded-xl border border-amber-500/30 bg-card">
        <table className="w-full text-sm">
          <thead className="bg-amber-500/10 text-left">
            <tr>
              <th className="px-3 py-2">पहलू</th>
              <th className="px-3 py-2">22 कैरेट</th>
              <th className="px-3 py-2">24 कैरेट</th>
            </tr>
          </thead>
          <tbody className="[&_tr]:border-t [&_tr]:border-amber-500/10">
            <tr><th className="px-3 py-2 text-left font-medium">शुद्धता</th><td className="px-3 py-2">91.6%</td><td className="px-3 py-2">99.9%</td></tr>
            <tr><th className="px-3 py-2 text-left font-medium">मुख्य उपयोग</th><td className="px-3 py-2">आभूषण, ज्वेलरी</td><td className="px-3 py-2">सिक्के, बिस्किट, निवेश</td></tr>
            <tr><th className="px-3 py-2 text-left font-medium">मेकिंग चार्ज</th><td className="px-3 py-2">₹350–₹700 प्रति ग्राम</td><td className="px-3 py-2">केवल मामूली रिफाइनिंग शुल्क</td></tr>
            <tr><th className="px-3 py-2 text-left font-medium">GST</th><td className="px-3 py-2">3% + मेकिंग पर 5%</td><td className="px-3 py-2">3% (सिक्का/बार पर)</td></tr>
            <tr><th className="px-3 py-2 text-left font-medium">रीसेल पर कटौती</th><td className="px-3 py-2">10–15% (मेकिंग वापस नहीं)</td><td className="px-3 py-2">2–3% (बुलियन मार्जिन)</td></tr>
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">नोट: मेकिंग चार्ज और GST राज्य व ज्वेलर के अनुसार बदल सकते हैं। ऊपर दी गयी लाइव तालिका केवल कच्चे धातु का दाम दर्शाती है।</p>
    </section>
  );
}

/** Silver rates strip for the DailyCombo template. */
export function SilverStrip() {
  const { data: silver } = useSilverRates();
  const perOz = silver.spotUsdPerOz * silver.inrPerUsd;
  const perKg = (perOz / GRAMS_PER_OZ) * 1000;
  const perGram = perKg / 1000;
  return (
    <section className="mx-auto max-w-6xl px-4 py-4">
      <div className="rounded-2xl border border-slate-400/30 bg-gradient-to-br from-slate-400/10 to-slate-500/5 p-4">
        <h2 className="text-lg font-bold mb-3">आज चांदी का लाइव भाव — भारत</h2>
        <div className="grid grid-cols-3 gap-3 text-center text-sm">
          <div className="rounded-lg border border-border bg-card p-3">
            <div className="text-xs text-muted-foreground">1 ग्राम चांदी</div>
            <div className="mt-1 font-extrabold tabular-nums">{formatMoney(perGram, "INR")}</div>
          </div>
          <div className="rounded-lg border border-border bg-card p-3">
            <div className="text-xs text-muted-foreground">1 किलो चांदी</div>
            <div className="mt-1 font-extrabold tabular-nums text-slate-700 dark:text-slate-200">{formatMoney(perKg, "INR")}</div>
          </div>
          <div className="rounded-lg border border-border bg-card p-3">
            <div className="text-xs text-muted-foreground">1 औंस चांदी</div>
            <div className="mt-1 font-extrabold tabular-nums">{formatMoney(perOz, "INR")}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/** City breakdown grid for RegionalCity template. */
export function CityRatesGrid() {
  const { data } = useGoldRates();
  const rows = INDIA_CITIES.map((c) => ({
    ...c,
    p: computeCityPrices({ spotUsdPerOz: data.spotUsdPerOz, fxRate: data.inrPerUsd, country: "india", cityPremium: c.premium }),
  }));
  const HI_NAME: Record<string, string> = {
    delhi: "दिल्ली", mumbai: "मुंबई", chennai: "चेन्नई", kolkata: "कोलकाता",
    bangalore: "बेंगलुरु", "hyderabad-in": "हैदराबाद", ahmedabad: "अहमदाबाद",
    pune: "पुणे", jaipur: "जयपुर", lucknow: "लखनऊ", surat: "सूरत",
    kanpur: "कानपुर", nagpur: "नागपुर", indore: "इंदौर", bhopal: "भोपाल",
    patna: "पटना", vadodara: "वडोदरा", ludhiana: "लुधियाना",
    coimbatore: "कोयंबटूर", visakhapatnam: "विशाखापत्तनम",
  };
  return (
    <section className="mx-auto max-w-6xl px-4 py-6">
      <h2 className="text-xl md:text-2xl font-bold mb-3">भारत के प्रमुख शहरों में आज सोने का भाव (22K / 24K प्रति 10 ग्राम)</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((c) => (
          <div key={c.slug} className="rounded-2xl border border-amber-500/25 bg-card p-4">
            <div className="font-semibold">{HI_NAME[c.slug] ?? c.name}</div>
            <div className="mt-1 text-sm text-muted-foreground tabular-nums">22K: <span className="font-bold text-amber-700 dark:text-amber-300">{formatMoney(c.p.per10g22, "INR")}</span></div>
            <div className="text-sm text-muted-foreground tabular-nums">24K: <span className="font-bold">{formatMoney(c.p.per10g24, "INR")}</span></div>
          </div>
        ))}
      </div>
    </section>
  );
}